import { HttpParams } from '@angular/common/http'
import { Component, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'

import {
    ListItem,
    RepositoryModal,
    TireVendor,
    TireVistaFirmContactSearchResult,
    TireVistaFirmSearchResult,
    TireVistaVendorSearchResult,
} from '../../shared'

type VistaSource = 'local' | 'apVendor' | 'firmContact'

@Component({
    templateUrl: './tire-vendor-modal.component.html',
    standalone: true,
    imports: [FormsModule, FaIconComponent, WjInputModule],
})
export class TireVendorModalComponent extends RepositoryModal<TireVendor> {
    sourceOptions = [
        new ListItem('local' as VistaSource, 'Local fallback only'),
        new ListItem('apVendor' as VistaSource, 'Vista AP Vendor (location)'),
        new ListItem('firmContact' as VistaSource, 'Vista Firm Contact'),
    ]

    source: VistaSource = 'local'
    private suppressSourceChange = true
    searchTerm = ''
    isSearching = false

    vendorResults = signal<TireVistaVendorSearchResult[]>([])
    firmResults = signal<TireVistaFirmSearchResult[]>([])
    selectedVendor: TireVistaVendorSearchResult | null = null
    selectedFirm: TireVistaFirmSearchResult | null = null
    selectedContact: TireVistaFirmContactSearchResult | null = null
    firmContactOptions: ListItem<number>[] = []

    protected override onInitialized() {
        if (this.target.vistaVendor != null) {
            this.source = 'apVendor'
            this.selectedVendor = {
                vendor: this.target.vistaVendor,
                name: this.target.resolvedLocation || this.target.name,
                contact: this.target.resolvedContactName,
                phone: this.target.resolvedPhone,
                email: this.target.resolvedEmail,
            }
        } else if (this.target.vistaFirmNumber != null) {
            this.source = 'firmContact'
            this.selectedFirm = {
                firmNumber: this.target.vistaFirmNumber,
                name: this.target.resolvedLocation || this.target.name,
                phone: this.target.resolvedPhone,
                email: this.target.resolvedEmail,
                contacts: [],
            }
            const contactCode = this.target.vistaFirmContactCode
            if (contactCode != null) {
                this.selectedContact = {
                    contactCode,
                    firstName: this.target.resolvedContactName,
                    phone: this.target.resolvedPhone,
                    email: this.target.resolvedEmail,
                }
                this.firmContactOptions = [
                    new ListItem(contactCode, this.target.resolvedContactName || `Contact ${contactCode}`),
                ]
            }
        } else {
            this.source = 'local'
        }
        // Allow user-driven source changes after initial binding settles.
        setTimeout(() => (this.suppressSourceChange = false))
    }

    onSourceChanged() {
        if (this.suppressSourceChange) return

        if (this.source === 'local') {
            this.clearVistaLinks()
            this.selectedVendor = null
            this.selectedFirm = null
            this.selectedContact = null
            this.vendorResults.set([])
            this.firmResults.set([])
        } else if (this.source === 'apVendor') {
            this.target.vistaFirmNumber = null
            this.target.vistaFirmContactCode = null
            this.selectedFirm = null
            this.selectedContact = null
            this.firmResults.set([])
        } else {
            this.target.vistaVendor = null
            this.selectedVendor = null
            this.vendorResults.set([])
        }
    }

    search() {
        const term = (this.searchTerm || '').trim()
        if (!term || this.source === 'local') return

        this.isSearching = true
        const params = new HttpParams().set('term', term)

        if (this.source === 'apVendor') {
            this.services.data.get<TireVistaVendorSearchResult[]>('api/TireVendor/VistaVendorSearch', params).subscribe({
                next: rows => {
                    this.vendorResults.set(rows ?? [])
                    this.isSearching = false
                },
                error: () => {
                    this.isSearching = false
                    this.services.notification.error('Failed to search Vista vendors')
                },
            })
        } else {
            this.services.data.get<TireVistaFirmSearchResult[]>('api/TireVendor/VistaFirmSearch', params).subscribe({
                next: rows => {
                    this.firmResults.set(rows ?? [])
                    this.isSearching = false
                },
                error: () => {
                    this.isSearching = false
                    this.services.notification.error('Failed to search Vista firms')
                },
            })
        }
    }

    selectVendor(row: TireVistaVendorSearchResult) {
        this.selectedVendor = row
        this.target.vistaVendor = row.vendor
        this.target.vistaFirmNumber = null
        this.target.vistaFirmContactCode = null
        if (!this.target.name?.trim()) {
            this.target.name = row.name
        }
        this.target.resolvedContactName = row.contact
        this.target.resolvedPhone = row.phone
        this.target.resolvedEmail = row.email
        this.target.resolvedLocation = this.formatLocation(row.name, row.city, row.state)
        this.target.contactSource = 'Vista AP Vendor'
    }

    selectFirm(row: TireVistaFirmSearchResult) {
        this.selectedFirm = row
        this.target.vistaVendor = null
        this.target.vistaFirmNumber = row.firmNumber
        this.selectedContact = null
        this.target.vistaFirmContactCode = null
        this.firmContactOptions = (row.contacts ?? []).map(
            c => new ListItem(c.contactCode, this.formatContactName(c) || `Contact ${c.contactCode}`),
        )
        if (!this.target.name?.trim()) {
            this.target.name = row.name
        }
        this.target.resolvedLocation = this.formatLocation(row.name, row.city, row.state)
        this.target.resolvedPhone = row.phone
        this.target.resolvedEmail = row.email
        this.target.resolvedContactName = undefined
        this.target.contactSource = 'Vista Firm Contact'
    }

    onFirmContactChanged() {
        if (!this.selectedFirm || this.target.vistaFirmContactCode == null) {
            this.selectedContact = null
            return
        }
        const contact = (this.selectedFirm.contacts ?? []).find(c => c.contactCode === this.target.vistaFirmContactCode) ?? null
        this.selectedContact = contact
        if (contact) {
            this.target.resolvedContactName = this.formatContactName(contact)
            this.target.resolvedPhone = contact.phone || contact.mobilePhone || this.selectedFirm.phone
            this.target.resolvedEmail = contact.email || this.selectedFirm.email
        }
    }

    clearVistaLinks() {
        this.target.vistaVendor = null
        this.target.vistaFirmNumber = null
        this.target.vistaFirmContactCode = null
        this.target.contactSource = 'Local fallback'
        this.target.resolvedContactName = this.target.contactName
        this.target.resolvedPhone = this.target.phone
        this.target.resolvedEmail = this.target.email
        this.target.resolvedLocation = this.target.name
    }

    get previewContactName() {
        return this.target.resolvedContactName || this.target.contactName || '—'
    }

    get previewPhone() {
        return this.target.resolvedPhone || this.target.phone || '—'
    }

    get previewEmail() {
        return this.target.resolvedEmail || this.target.email || '—'
    }

    get previewLocation() {
        return this.target.resolvedLocation || this.target.name || '—'
    }

    get previewSource() {
        return this.target.contactSource || (this.source === 'local' ? 'Local fallback' : '—')
    }

    private formatContactName(c: TireVistaFirmContactSearchResult) {
        return [c.firstName, c.lastName].filter(x => !!x?.trim()).join(' ')
    }

    private formatLocation(name?: string, city?: string, state?: string) {
        const cityState = [city, state].filter(x => !!x?.trim()).join(', ')
        if (!cityState) return name || ''
        if (!name) return cityState
        return `${name} (${cityState})`
    }
}
